import React, { useEffect, useState } from "react";
import { integrationLabel } from "../../../utils/integrations";
import { Button, Card } from "../../ui";
import MaintenanceClosureModal from "../../modals/MaintenanceClosureModal";
import RequestActionModals from "../../modals/RequestActionModals";
import UnifiedRequests from "./unified/UnifiedRequests";
import SubscriptionLockNotice from "../../subscription/SubscriptionLockNotice";
import "./Requests.css";
const ACTION_TYPES = [
  { key: "statement", ar: "كشف حساب", en: "Statement" },
  { key: "taxInvoice", ar: "فاتورة ضريبية", en: "Tax Invoice" },
  { key: "contract", ar: "عقد", en: "Contract" },
  {
    key: "contactManager",
    ar: "تواصل مدير الحساب",
    en: "Contact Account Manager",
  },
  { key: "maintenance", ar: "إغلاق صيانة", en: "Maintenance Closure" },
];
const initialForms = {
  statement: { app: "jahez", from: "", to: "" },
  taxInvoice: { app: "jahez", month: "" },
  contract: { app: "jahez", note: "" },
  contactManager: { app: "jahez" },
  maintenance: { branch: "فرع الروضة", from: "", to: "" },
};
// The Requests card of the dashboard and of the cashier page: the shortcut buttons (when
// shown) and the one unified list (In Progress / Completed). What a viewer sees and may do
// is decided by the server; a cashier is only ever given the requests they raised.
// `lock` ({ reason }): operational requests need an active subscription - the shortcuts are disabled and explained, the list stays readable.
export default function Requests({
  lang,
  text,
  lock = null,
  onHiddenChange,
  onOpenRequestFile,
  showShortcuts = true,
  branches = [],
  applications = [],
  onCreateRequest,
  stores = [],
  selectedStoreId = "",
  highlightIds = [],
  accounts = [],
  pricing = null,
  onChanged,
  reloadKey,
}) {
  const APP_OPTIONS = applications.map((application) => ({
    value: application.key || application.nameAr,
    label:
      lang === "ar"
        ? application.nameAr
        : application.nameEn || application.nameAr,
  }));
  const [openModal, setOpenModal] = useState("");
  // Without an active subscription (`lock`) the section is shown only when there are requests to read (read-only, shortcuts disabled).
  // The list keeps loading while hidden, and loading / error states are never hidden.
  const [listState, setListState] = useState("loading");
  const hidden = Boolean(lock) && listState === "empty";
  useEffect(() => {
    onHiddenChange?.(hidden);
  }, [hidden, onHiddenChange]);
  const [forms, setForms] = useState(initialForms);
  const updateForm = (formName, field, value) => {
    setForms((current) => ({
      ...current,
      [formName]: { ...current[formName], [field]: value },
    }));
  };
  const closeRequestModal = () => setOpenModal("");
  const sendRequest = () => {
    const requestMap = {
      statement: {
        type: "كشف حساب",
        target: integrationLabel(forms.statement.app, "ar"),
        details: `طلب كشف حساب من ${forms.statement.from || "—"} إلى ${forms.statement.to || "—"}`,
        payload: {
          action: "statementRequest",
          ...forms.statement,
        },
      },
      taxInvoice: {
        type: "فاتورة ضريبية",
        target: integrationLabel(forms.taxInvoice.app, "ar"),
        details: `طلب فاتورة ضريبية للشهر ${forms.taxInvoice.month || "—"}`,
        payload: {
          action: "taxInvoiceRequest",
          ...forms.taxInvoice,
        },
      },
      contract: {
        type: "عقد",
        target: integrationLabel(forms.contract.app, "ar"),
        details:
          forms.contract.note ||
          `طلب عقد ${integrationLabel(forms.contract.app, "ar")}`,
        payload: {
          action: "contractRequest",
          ...forms.contract,
        },
      },
      contactManager: {
        type: "تواصل مدير الحساب",
        target: integrationLabel(forms.contactManager.app, "ar"),
        details: `طلب تواصل مدير حساب ${integrationLabel(forms.contactManager.app, "ar")}`,
        payload: {
          action: "contactManagerRequest",
          ...forms.contactManager,
        },
      },
    };

    const request = requestMap[openModal];

    if (request) {
      onCreateRequest?.(request);
    }

    closeRequestModal();
  };
  const modalFooter = (
    <>
      <Button variant="primary" appearance="filled" onClick={sendRequest}>
        {lang === "ar" ? "إرسال الطلب" : "Send Request"}
      </Button>
      <Button variant="danger" appearance="soft" onClick={closeRequestModal}>
        {text.cancel}
      </Button>
    </>
  );
  const title = (ar, en) =>
    `${lang === "ar" ? "طلب" : "Request"} ${lang === "ar" ? ar : en}`;
  const shortcutsNode = showShortcuts ? (
          <div className="lud-request-shortcuts">
            {ACTION_TYPES.map((action) => (
              <Button
                key={action.key}
                size="sm"
                variant="neutral"
                appearance="outline"
                disabled={Boolean(lock)}
                title={lock ? (lang === "ar" ? "يتطلب اشتراكًا فعّالًا" : "Needs an active subscription") : undefined}
                onClick={() => setOpenModal(action.key)}
              >
                {lang === "ar" ? action.ar : action.en}
              </Button>
            ))}
          </div>
  ) : null;
  return (
    <section className="lud-dashboard-section" hidden={hidden}>
      <Card className="lud-requests-card lud-requests-card--unified">
        {lock && <SubscriptionLockNotice lang={lang} reason={lock.reason} title={lang === "ar" ? "الطلبات التشغيلية" : "Operational requests"} />}
        <UnifiedRequests
          lang={lang}
          stores={stores}
          selectedStoreId={selectedStoreId}
          highlightIds={highlightIds}
          branches={branches}
          applications={applications}
          accounts={accounts}
          pricing={pricing}
          onChanged={onChanged}
          onOpenRequestFile={onOpenRequestFile}
          reloadKey={reloadKey}
          shortcuts={shortcutsNode}
          onListState={setListState}
        />
      </Card>

      <RequestActionModals
        openModal={openModal}
        lang={lang}
        forms={forms}
        appOptions={APP_OPTIONS}
        onClose={closeRequestModal}
        footer={modalFooter}
        title={title}
        updateForm={updateForm}
      />

      <MaintenanceClosureModal
        open={openModal === "maintenance"}
        lang={lang}
        text={text}
        branches={branches}
        onClose={closeRequestModal}
        onSubmit={({ branch, from, to, reason }) => {
          onCreateRequest?.({
            type: "إغلاق صيانة",
            target: branch?.nameAr || "",
            details: `طلب إغلاق للصيانة — من ${from} إلى ${to}`,
            payload: {
              action: "maintenanceClosure",
              branchId: branch?.id,
              from,
              to,
              ...(reason ? { reason } : {}),
            },
          });
          closeRequestModal();
        }}
      />
    </section>
  );
}
